/**
 * nosto-empty-placements.js  (MFS-532)
 *
 * Hides recommendation sections whose Nosto placement returns nothing.
 *
 * Why this is needed: the section heading ("You May Also Like", "Products You
 * Recently Viewed") is rendered by the THEME in Liquid, while the products come
 * from a Nosto app block inside it. When Nosto returns no recommendations the
 * heading is left standing over an empty div. That is live on production today
 * on every market, because "recently viewed" is legitimately empty for a visitor
 * with no browsing history, and it becomes permanent on any market where Nosto
 * is switched off (see snippets/nosto-enabled-boolean.liquid).
 *
 * Why JS and not CSS or Liquid:
 *   - Liquid cannot identify the block. A section using `{% content_for 'blocks' %}`
 *     reports app blocks as the generic type "@app", so the theme cannot tell a
 *     Nosto placement from a Klaviyo one (measured, MFS-532).
 *   - CSS `:empty` is wrong. Nosto injects its carousel <style> before the
 *     products arrive, so the placement is non-empty while still showing nothing.
 *   - The theme's own Nosto kill switch is the wrong signal too: on a gated market
 *     Nosto still serves the placements (its core script is a separate app embed),
 *     so keying off it would delete working carousels.
 *
 * So the only reliable signal is what the placement actually rendered, checked
 * after Nosto has had time to fill it. Same approach as the cart's Shopify
 * fallback in personalised-cards-shopify.js.
 */
(function () {
  'use strict';

  // Matches the cart fallback's default so both wait the same amount before
  // deciding Nosto is not coming.
  var TIMEOUT = 4000;
  var HIDDEN_ATTR = 'data-nosto-empty-hidden';

  function placements() {
    var all = document.querySelectorAll('.nosto_element');
    var out = [];
    for (var i = 0; i < all.length; i++) {
      // The cart's personalised-cards block has its own richer fallback: it clones
      // a Shopify product grid in rather than hiding anything. Leave those alone.
      if (document.querySelector('[data-nosto-target="' + all[i].id + '"]')) continue;
      out.push(all[i]);
    }
    return out;
  }

  function hasContent(el) {
    if (!el) return false;

    // Rendered products, or the scaffold Nosto emits BEFORE they exist.
    //
    // `nosto-dynamic-card` and the splide carousel wrappers are what
    // nosto/templates/recommendations/*-dynamic-cards.html emit while their product cards are
    // still being fetched over the Section Rendering API: at that point the placement holds no
    // links and no images at all. Treating that as empty would hide the section, and a hidden
    // section can never intersect the viewport, so the lazy hydration would never fire and a
    // working carousel would be removed permanently. Hence they count as content.
    if (el.querySelector(
      'a[href], img, nosto-dynamic-card, [class*="splide"], .nosto-block, .product-card, .ns-product'
    )) {
      return true;
    }

    // Catch-all for any other markup Nosto renders: a structural child that is not just an
    // injected <style> or <script>. A placement Nosto returned nothing for is literally an
    // empty div (measured on production: 82 bytes, no children), so this stays specific.
    //
    // Deliberately biased towards NOT hiding: leaving a heading above an empty section is the
    // pre-existing behaviour and merely untidy, whereas hiding a working carousel loses
    // revenue and cannot recover itself.
    for (var i = 0; i < el.children.length; i++) {
      var tag = el.children[i].tagName;
      if (tag !== 'STYLE' && tag !== 'SCRIPT') return true;
    }

    // Text-only placements (a banner or message rather than products).
    return strippedText(el) !== '';
  }

  function strippedText(el) {
    var clone = el.cloneNode(true);
    var drop = clone.querySelectorAll('style, script');
    for (var i = 0; i < drop.length; i++) drop[i].parentNode.removeChild(drop[i]);
    return (clone.textContent || '').trim();
  }

  function isInsideOneOf(node, list) {
    for (var i = 0; i < list.length; i++) {
      if (list[i].contains(node)) return true;
    }
    return false;
  }

  /**
   * True when everything in `el` is either a heading or one of the empty
   * placements, i.e. hiding it removes no content the customer could have used.
   */
  function isEmptyish(el, emptyOnes) {
    // A placement that is NOT in the empty set has content (or scaffold that is about to
    // become content), so anything containing one must survive. Without this, a section
    // holding one dead placement beside one working carousel would be hidden wholesale,
    // because an unhydrated carousel has no links or images of its own yet.
    var placements = el.querySelectorAll('.nosto_element');
    for (var p = 0; p < placements.length; p++) {
      if (!isInsideOneOf(placements[p], emptyOnes)) return false;
    }

    var interactive = el.querySelectorAll(
      'a[href], img, button, input, select, textarea, video, iframe, nosto-dynamic-card, [class*="splide"]'
    );
    for (var i = 0; i < interactive.length; i++) {
      if (!isInsideOneOf(interactive[i], emptyOnes)) return false;
    }

    var clone = el.cloneNode(true);
    var drop = clone.querySelectorAll('h1, h2, h3, h4, h5, h6, style, script');
    for (var j = 0; j < drop.length; j++) drop[j].parentNode.removeChild(drop[j]);
    return (clone.textContent || '').trim() === '';
  }

  /**
   * Walk up from the placement and return the outermost ancestor that holds
   * nothing but headings and empty placements, stopping at the section boundary.
   * For a blocks-container whose only block is the placement that is the whole
   * section, heading included. For the add-to-cart popup, where the Nosto blocks
   * sit alongside real ones, it stops at the sub-wrapper and leaves the rest.
   */
  function targetToHide(placement, emptyOnes) {
    var best = null;
    var node = placement.parentElement;

    while (node && node !== document.body) {
      if (!isEmptyish(node, emptyOnes)) break;
      best = node;
      if (node.id && node.id.indexOf('shopify-section') === 0) break;
      node = node.parentElement;
    }

    return best || placement;
  }

  function apply() {
    var all = placements();
    if (!all.length) return;

    var empties = [];
    for (var i = 0; i < all.length; i++) {
      if (!hasContent(all[i])) empties.push(all[i]);
    }

    for (var j = 0; j < empties.length; j++) {
      var target = targetToHide(empties[j], empties);
      if (target.getAttribute(HIDDEN_ATTR) === 'true') continue;
      target.setAttribute(HIDDEN_ATTR, 'true');
      target.style.display = 'none';
    }
  }

  /**
   * Nosto can fill a placement late (lazy carousels only render once scrolled
   * into view), so a section hidden on the timeout is restored if content
   * arrives afterwards. Cheaper and less surprising than leaving it hidden.
   */
  function watch() {
    var all = placements();
    for (var i = 0; i < all.length; i++) {
      observe(all[i]);
    }
  }

  function observe(placement) {
    if (placement.getAttribute('data-nosto-empty-observed') === 'true') return;
    placement.setAttribute('data-nosto-empty-observed', 'true');

    new MutationObserver(function () {
      if (!hasContent(placement)) return;
      var node = placement;
      while (node && node !== document.body) {
        if (node.getAttribute && node.getAttribute(HIDDEN_ATTR) === 'true') {
          node.removeAttribute(HIDDEN_ATTR);
          node.style.display = '';
        }
        node = node.parentElement;
      }
    }).observe(placement, { childList: true, subtree: true });
  }

  function init() {
    watch();
    window.setTimeout(apply, TIMEOUT);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Cart and other sections are re-rendered by innerHTML swaps, which replaces
  // the placements with fresh empty ones and drops our attributes. Re-arm after
  // a swap so a section that came back empty is hidden again.
  document.addEventListener('cart:sections:swapped', init);
})();
